import { useEffect, useRef, useState } from "react";
import { Play, Pause, X, Wand2 } from "lucide-react";

// Elegir el inicio del clip (TikTok / vista previa) con minuto + segundo, sin escribir HH:MM:SS.
// Guarda el valor en el mismo formato de siempre: "00:MM:SS".
const toSec = (v) => {
  const m = /^(\d{1,2}):(\d{2}):(\d{2})$/.exec(v || "");
  return m ? (+m[1]) * 3600 + (+m[2]) * 60 + (+m[3]) : null;
};
const toHMS = (s) => {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), x = Math.floor(s % 60);
  return [h, m, x].map((n) => String(n).padStart(2, "0")).join(":");
};
const pad = (n) => String(n).padStart(2, "0");

// Busca el tramo de 15 s con más energía (suele ser el coro/hook), sin servidor ni IA:
// se analiza el audio en el navegador. Evita los primeros 10 s y los últimos 15 s.
export async function suggestClipStart(audioUrl, windowSec = 15) {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx || !audioUrl) return null;
  const buf = await (await fetch(audioUrl)).arrayBuffer();
  const ctx = new Ctx();
  try {
    const audio = await ctx.decodeAudioData(buf);
    const sr = audio.sampleRate, n = audio.length, chans = Math.min(2, audio.numberOfChannels);
    const secs = Math.floor(n / sr);
    if (secs < windowSec + 20) return null;
    const rms = new Float64Array(secs);
    const data = Array.from({ length: chans }, (_, c) => audio.getChannelData(c));
    const step = 8; // muestreo parcial: rápido y suficiente para medir energía
    for (let s = 0; s < secs; s++) {
      let acc = 0, cnt = 0;
      for (let i = s * sr; i < (s + 1) * sr; i += step) {
        for (let c = 0; c < chans; c++) { const v = data[c][i]; acc += v * v; }
        cnt += chans;
      }
      rms[s] = Math.sqrt(acc / Math.max(1, cnt));
    }
    let best = null, bestVal = -1;
    for (let start = 10; start + windowSec <= secs - 15; start++) {
      let sum = 0;
      for (let k = 0; k < windowSec; k++) sum += rms[start + k];
      if (sum > bestVal) { bestVal = sum; best = start; }
    }
    return best;
  } finally {
    ctx.close?.();
  }
}

export default function TimeStartPicker({ value, onChange, audioUrl, testId }) {
  const [duration, setDuration] = useState(0);
  const [suggesting, setSuggesting] = useState(false);
  const [suggested, setSuggested] = useState(false);
  const autoTried = useRef("");
  const [playing, setPlaying] = useState(false);
  const audio = useRef(null);
  const stopTimer = useRef(null);

  // Largo real de la canción (si ya subió el audio) para limitar los selectores.
  useEffect(() => {
    setDuration(0);
    if (!audioUrl) return undefined;
    const a = new Audio();
    a.preload = "metadata";
    a.src = audioUrl;
    const ok = () => { if (isFinite(a.duration)) setDuration(Math.floor(a.duration)); };
    a.addEventListener("loadedmetadata", ok);
    return () => { a.removeEventListener("loadedmetadata", ok); a.src = ""; };
  }, [audioUrl]);

  useEffect(() => () => { clearTimeout(stopTimer.current); audio.current?.pause(); }, []);

  const suggest = async () => {
    if (!audioUrl || suggesting) return;
    setSuggesting(true);
    try {
      const s = await suggestClipStart(audioUrl);
      if (s !== null) { onChange(toHMS(s)); setSuggested(true); }
    } catch { /* si el navegador no puede analizarlo, el artista lo elige a mano */ }
    finally { setSuggesting(false); }
  };

  // Al subir el audio, si no hay inicio elegido, se sugiere solo (una vez por audio).
  useEffect(() => {
    if (audioUrl && !value && autoTried.current !== audioUrl) { autoTried.current = audioUrl; suggest(); }
  }, [audioUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = toSec(value);
  const sec = total ?? 0;
  const maxMin = duration ? Math.floor(duration / 60) : 9;
  const set = (s) => {
    const lim = duration ? Math.max(0, duration - 1) : 599;
    onChange(toHMS(Math.max(0, Math.min(s, lim))));
  };
  const curMin = Math.floor(sec / 60);
  const curSec = sec % 60;
  const maxSecForMin = duration && curMin === maxMin ? Math.max(0, (duration - 1) % 60) : 59;

  // Escuchar 15 s desde el punto elegido.
  const preview = () => {
    if (!audioUrl) return;
    if (playing) { audio.current?.pause(); clearTimeout(stopTimer.current); setPlaying(false); return; }
    if (!audio.current) audio.current = new Audio();
    audio.current.src = audioUrl;
    audio.current.currentTime = sec;
    audio.current.play().then(() => {
      setPlaying(true);
      stopTimer.current = setTimeout(() => { audio.current?.pause(); setPlaying(false); }, 15000);
    }).catch(() => setPlaying(false));
  };

  const selCls = "rounded-lg border border-slate-300 bg-white px-2 py-2 font-mono text-sm outline-none focus:border-black";
  return (
    <div className="min-w-0 space-y-2" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-2">
        <select aria-label="Minuto" value={total === null ? "" : curMin} onChange={(e) => setSuggested(false) || set((+e.target.value) * 60 + (total === null ? 0 : Math.min(curSec, 59)))} className={selCls} data-testid={`${testId}-min`}>
          {total === null && <option value="">min</option>}
          {Array.from({ length: maxMin + 1 }, (_, m) => <option key={m} value={m}>{m} min</option>)}
        </select>
        <select aria-label="Segundo" value={total === null ? "" : curSec} onChange={(e) => setSuggested(false) || set(curMin * 60 + (+e.target.value))} className={selCls} data-testid={`${testId}-sec`}>
          {total === null && <option value="">seg</option>}
          {Array.from({ length: maxSecForMin + 1 }, (_, x) => <option key={x} value={x}>{pad(x)} s</option>)}
        </select>
        {audioUrl && total !== null && (
          <button type="button" onClick={preview} className="flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-2 text-xs font-semibold hover:border-black" title="Escuchar 15 s desde aquí">
            {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />} {playing ? "Parar" : "Escuchar"}
          </button>
        )}
        {audioUrl && (
          <button type="button" onClick={suggest} disabled={suggesting} className="flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-2 text-xs font-semibold hover:border-black disabled:opacity-50" title="Buscar el momento con más energía (suele ser el coro)">
            <Wand2 className="h-3.5 w-3.5" /> {suggesting ? "Analizando…" : "Sugerir"}
          </button>
        )}
        {total !== null && (
          <button type="button" onClick={() => { audio.current?.pause(); setPlaying(false); setSuggested(false); onChange(""); }} className="text-slate-400 hover:text-red-500" title="Quitar"><X className="h-4 w-4" /></button>
        )}
      </div>
      {suggested && total !== null && <p className="text-[11px] text-emerald-700">Sugerido automáticamente: el tramo con más energía (suele ser el coro). Escúchalo y cámbialo si prefieres otro.</p>}
      {duration > 0 && (
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] text-slate-400">0:00</span>
          <input type="range" min={0} max={Math.max(0, duration - 1)} step={1} value={sec} onChange={(e) => setSuggested(false) || set(+e.target.value)} className="h-1.5 flex-1 accent-[#AC3333]" aria-label="Inicio del clip" />
          <span className="font-mono text-[10px] text-slate-400">{Math.floor(duration / 60)}:{pad(duration % 60)}</span>
        </div>
      )}
    </div>
  );
}
